import {ChargerTypes, evChargerTemplate} from './evChargerConstants';

/** Maps template keys → form field labels. */
const fieldLabels = {
  ip: 'IP Address',
  port: 'Port',
  modbusId: 'Modbus ID',
  minCurrent: 'Minimum Charging Current [A]',
  minPowerW: 'Minimum Power [W]',
  installationId: 'Installation ID',
  modbusId: 'Modbus ID',
  offlineTimeout: 'Offline Timeout [s]',
  offlinePowerA: 'Offline Power [A]',
  offlinePower: 'Offline Power [W]',
};

/** Maps template keys → tree-node summary labels. */
const summaryLabels = {
  ip: 'IP',
  socketId: 'Socket ID',
  circuitId: 'CircuitId',
  siteId: 'SiteId',
  installationId: 'InstallationId',
  modbusId: 'Modbus ID',
};

// ─── Registry ────────────────────────────────────────────────────────────────

/**
 * EV charger registry — single source of truth for charger type metadata.
 *
 * To add a new charger type:
 *   1. Add the key to ChargerTypes + template in evChargerConstants.js
 *   2. Add one entry here.
 *   3. Done.
 *
 * Everything else (credentials, minPower, easee) is inferred from the template.
 */
export const chargerRegistry = [
  {type: ChargerTypes.ALFEN, label: 'Alfen SCN', fields: ['ip'], summary: ['ip']},
  {
    type: ChargerTypes.ALFEN_SOCKET,
    label: 'Alfen Socket',
    fields: ['ip', 'port', 'minCurrent'],
    summary: ['ip', 'socketId'],
    socket: [1, 2]
  },
  {type: ChargerTypes.ALPITRONIC, label: 'Alpitronic HYC', fields: ['ip'], summary: ['ip']},
  {
    type: ChargerTypes.AUTEL,
    label: 'Autel',
    fields: ['ip', 'port', 'minCurrent'],
    summary: ['ip', 'socketId'],
    socket: [0, 1000]
  },
  {
    type: ChargerTypes.DEFA,
    label: 'DEFA Power',
    fields: ['ip', 'port', 'offlineTimeout', 'offlinePowerA', 'minCurrent'],
    summary: ['ip']
  },
  {
    type: ChargerTypes.NEXBLUE,
    label: 'NexBlue',
    fields: ['ip', 'minCurrent', 'offlineTimeout', 'offlinePowerA'],
    summary: ['ip']
  },
  {
    type: ChargerTypes.EASEE,
    label: 'Easee Circuit',
    fields: [],
    summary: ['circuitId', 'siteId']
  },
  {
    type: ChargerTypes.TECHNAGON_AC,
    label: 'Technagon AC',
    fields: ['ip', 'port', 'minCurrent'],
    summary: ['ip', 'socketId'],
    socket: {values: [1, 2], labels: ['Charging Point 1', 'Charging Point 2']}
  },
  {
    type: ChargerTypes.SUPERNOVA,
    label: 'Wallbox Supernova',
    fields: ['ip'],
    summary: ['ip']
  },
  {
    type: ChargerTypes.ZAPTEC,
    label: 'Zaptec Installation',
    fields: ['installationId'],
    summary: ['installationId']
  },
  {
    type: ChargerTypes.KEMPOWER,
    label: 'Kempower',
    fields: ['ip', 'port', 'modbusId', 'offlineTimeout', 'minPowerW',  'offlinePower'],
    summary: ['ip']
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Resolve a field key to {label, name, type} using the given charger's template. */
const resolveField = (chargerType) => (key) => ({
  label: fieldLabels[key] || key,
  name: key,
  type: Object.values(evChargerTemplate).some((template) => typeof template?.[key] === 'number') ? 'number' : 'text',
});

/** Resolve a summary key to {label, key}. */
const resolveSummary = (key) => ({label: summaryLabels[key] || key, key});

/** Normalize socket config: array → object with default labels. */
const resolveSocket = (socket) => {
  if (!socket) return null;
  if (Array.isArray(socket)) return {values: socket, labels: ['Left Socket', 'Right Socket']};
  return {values: socket.values, labels: socket.labels || ['Left Socket', 'Right Socket']};
};

/**
 * Look up a charger entry by type, with resolved fields/summary/socket.
 * Returns null if type is not found.
 */
export const getChargerEntry = (type) => {
  const raw = chargerRegistry.find((c) => c.type === type);
  if (!raw) return null;
  return {
    ...raw,
    resolvedFields: raw.fields.map(resolveField(type)),
    resolvedSummary: raw.summary.map(resolveSummary),
    resolvedSocket: resolveSocket(raw.socket),
  };
};
